<template>
  <div class="d-flex news-container">
    <div class="news-sidebar">
      <div class="sidebar-header">
        {{ $t('news.show') }}
      </div>
      <div
        class="channel-item justify-content-between"
        :class="{ active: !unreadOnly }"
        @click="unreadOnly = false"
      >
        <span><i class="far fa-list mr-2" />{{ $t('news.all') }}</span>
        <span class="small">{{ items.length }}</span>
      </div>
      <div
        class="channel-item justify-content-between"
        :class="{ active: unreadOnly }"
        @click="unreadOnly = true"
      >
        <span><i class="far fa-circle-dot mr-2" />{{ $t('news.unread') }}</span>
        <span class="small">{{ unreadCount }}</span>
      </div>

      <hr class="my-2">

      <div
        class="sidebar-header d-flex justify-content-between align-items-center clickable"
        @click="categoriesOpen = !categoriesOpen"
      >
        <span>
          {{ $t('news.categoriesTitle') }}
          <span
            v-if="!categoriesOpen"
            class="selected-category ml-1"
          >{{ category ? $t(`news.categories.${category}`) : $t('news.all') }}</span>
        </span>
        <i :class="categoriesOpen ? 'far fa-chevron-up' : 'far fa-chevron-down'" />
      </div>
      <template v-if="categoriesOpen">
        <div
          v-for="c in categories"
          :key="c"
          class="channel-item justify-content-between"
          :class="{ active: category === c }"
          @click="category = category === c ? null : c"
        >
          <span>{{ $t(`news.categories.${c}`) }}</span>
          <span class="small">{{ categoryCount(c) }}</span>
        </div>
      </template>

      <hr class="my-2">

      <div class="sidebar-header">
        {{ $t('news.listTitle') }}
      </div>
      <div
        v-if="loading"
        class="text-center py-3"
      >
        <Loader size="20px" />
      </div>
      <div
        v-else-if="filteredItems.length === 0"
        class="text-secondary small py-2"
      >
        {{ $t('news.empty') }}
      </div>
      <div
        v-for="item in filteredItems"
        :key="item.id"
        class="news-thread"
        :class="{ active: selected && selected.id === item.id, unread: item.unread }"
        @click="select(item.id)"
      >
        <div class="flex-grow-1">
          <NewsLabels :item="item" />
          <div class="news-thread-title">
            {{ item.title }}
          </div>
        </div>
        <span
          v-if="item.unread"
          class="news-unread-dot"
        />
      </div>
    </div>

    <div class="news-content flex-grow-1">
      <div
        v-if="loadingItem"
        class="text-center py-4"
      >
        <Loader />
      </div>
      <div
        v-else-if="!selected"
        class="text-center text-secondary py-4"
      >
        {{ loading ? '' : $t('news.empty') }}
      </div>
      <article v-else>
        <NewsLabels
          :item="selected"
          long-date
        />
        <h2 class="news-title mt-2">
          {{ selected.title }}
        </h2>
        <p class="news-summary">
          {{ selected.summary }}
        </p>
        <img
          v-if="selected.thumbnail && isImage(selected.thumbnail.path)"
          :src="selected.thumbnail.url"
          class="news-cover mb-3"
        >
        <div
          class="news-body"
          v-html="body"
        />

        <div
          v-if="files.length > 0"
          class="mt-3"
        >
          <a
            v-for="(m, idx) in files"
            :key="idx"
            :href="m.url"
            target="_blank"
            rel="noopener noreferrer"
            class="mr-3 small"
          >
            <i class="far fa-paperclip mr-1" />{{ fileName(m.path) }}
          </a>
        </div>

        <hr>
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div
            v-if="selected.helpful === null || selected.helpful === undefined"
            class="d-flex align-items-center"
          >
            <span class="small text-secondary mr-2">{{ $t('news.helpfulQuestion') }}</span>
            <button
              class="btn btn-sm btn-outline-primary mr-2"
              @click="feedback(true)"
            >
              {{ $t('general.yes') }}
            </button>
            <button
              class="btn btn-sm btn-outline-primary"
              @click="feedback(false)"
            >
              {{ $t('general.no') }}
            </button>
          </div>
          <div
            v-else
            class="small text-success"
          >
            <i class="far fa-circle-check mr-1" />
            {{ $t('news.helpfulThanks') }}
          </div>
          <a
            v-if="nextItem"
            href="#"
            class="small"
            @click.prevent="select(nextItem.id)"
          >
            {{ $t('news.next', { title: nextItem.title }) }}
            <i class="far fa-arrow-right ml-1" />
          </a>
        </div>
      </article>
    </div>
  </div>
</template>

<script>
import NewsLabels from '@/components/news/NewsLabels';
import sanitizeHtml from '@/utils/sanitizeHtml';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';

export default {
  props: {
    postId: String,
  },
  data: () => ({
    items: [],
    selected: null,
    loading: false,
    loadingItem: false,
    unreadOnly: false,
    category: null,
    categoriesOpen: false,
    categories: ['New', 'Improvement', 'Change', 'Important'],
  }),
  components: {
    NewsLabels,
  },
  computed: {
    ...mapGetters(['payeeId']),
    unreadCount() {
      return this.items.filter((x) => x.unread).length;
    },
    filteredItems() {
      return this.items
        .filter((x) => !this.unreadOnly || x.unread || (this.selected && x.id === this.selected.id))
        .filter((x) => !this.category || x.category === this.category);
    },
    body() {
      return sanitizeHtml(this.selected?.body);
    },
    files() {
      return (this.selected?.media || []).filter((m) => !this.isImage(m.path));
    },
    nextItem() {
      if (!this.selected) return null;
      const idx = this.filteredItems.findIndex((x) => x.id === this.selected.id);
      return idx >= 0 ? this.filteredItems[idx + 1] || null : null;
    },
  },
  watch: {
    postId(id) {
      if (id && (!this.selected || this.selected.id !== id)) {
        this.loadItem(id);
      }
    },
  },
  methods: {
    ...mapActions(['getNews', 'getNewsItem', 'openNews', 'sendNewsFeedback']),
    isImage(path) {
      return /\.(jpe?g|png|gif|webp|svg|bmp)$/i.test(path || '');
    },
    fileName(path) {
      return (path || '').split('/').pop();
    },
    categoryCount(c) {
      return this.items.filter((x) => x.category === c).length;
    },
    newestId() {
      // Default: the most recently published announcement (not the pinned one).
      const newest = this.items
        .slice()
        .sort((a, b) => moment(b.timestamp).valueOf() - moment(a.timestamp).valueOf())[0];
      return newest ? newest.id : null;
    },
    async load() {
      this.loading = true;
      try {
        const data = await this.getNews();
        this.items = data.list || [];
      } catch {
        this.items = [];
      } finally {
        this.loading = false;
      }

      const id = this.postId || this.newestId();
      if (id) {
        await this.loadItem(id);
      }
    },
    select(id) {
      const path = `/payee/${this.payeeId}/messages/news/${id}`;
      if (this.$route.path !== path) {
        this.$router.push(path);
      } else {
        this.loadItem(id);
      }
    },
    async loadItem(id) {
      this.loadingItem = true;
      try {
        this.selected = await this.getNewsItem(id);
      } catch {
        this.selected = null;
      } finally {
        this.loadingItem = false;
      }

      const listItem = this.items.find((x) => x.id === id);
      if (this.selected && listItem && listItem.unread) {
        listItem.unread = false;
        this.openNews(id).catch(() => {});
      }
    },
    async feedback(helpful) {
      const { id } = this.selected;
      this.selected = { ...this.selected, helpful };
      try {
        await this.sendNewsFeedback({ postId: id, helpful });
      } catch {
        // keep the thank-you state; the next load shows the stored value
      }
    },
  },
  created() {
    this.$emit('page', 'news');
    this.load();
  },
};
</script>

<style lang="scss" scoped>
@import '~@/styles/vars';

.news-container {
  min-height: calc(100vh - 160px);
}

.news-sidebar {
  width: 340px;
  min-width: 340px;
  border-right: 1px solid #eee;
  padding-right: 15px;
}

.sidebar-header {
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  color: #888;
  margin: 6px 0 8px;
}

.clickable {
  cursor: pointer;
}

.selected-category {
  text-transform: none;
  font-weight: 500;
  background: #f1f3f5;
  color: #606770;
  border-radius: 999px;
  padding: 0 7px;
}

.channel-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    background-color: #f6f6f6;
  }

  &.active {
    background-color: rgba($blue, 0.1);
    color: $blue;
  }
}

.news-thread {
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  margin-bottom: 2px;

  &:hover {
    background-color: #f6f6f6;
  }

  &.unread {
    background-color: rgba($blue, 0.05);
  }

  &.active {
    background-color: rgba(#333, 0.1);
  }
}

.news-thread-title {
  font-size: 14px;
  font-weight: 500;
  margin-top: 3px;
}

.news-unread-dot {
  width: 8px;
  height: 8px;
  min-width: 8px;
  border-radius: 50%;
  background: $blue;
  margin-top: 6px;
}

.news-content {
  padding-left: 30px;
  max-width: 900px;
}

.news-title {
  font-size: 26px;
  font-weight: 500;
}

.news-summary {
  font-size: 16px;
  color: #666;
}

.news-cover {
  width: 100%;
  max-height: 320px;
  object-fit: cover;
  border-radius: 15px;
}

.news-body {
  font-size: 15px;
  overflow-wrap: break-word;

  ::v-deep {
    img {
      max-width: 100%;
      border-radius: 6px;
    }

    blockquote {
      border-left: 3px solid #ddd;
      padding-left: 12px;
      color: #666;
    }
  }
}
</style>
